import { useState } from "react";
import "./QualityView.css";
import { Trans } from "react-i18next";

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`chevron ${isOpen ? "open" : ""}`}
    viewBox="0 0 24 24"
    width="16"
    height="16"
  >
    <path
      fill="currentColor"
      d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
    />
  </svg>
);

const StatusIcon = ({ status }) => {
  if (status === "pass" || status === "ok") {
    return <span className="icon-box pass">✔</span>;
  }
  if (status === "fail") {
    return <span className="icon-box fail">✘</span>;
  }
  return <span className="icon-box warn">⚠</span>;
};

const ValidationGroup = ({ section, translations }) => {
  const [isOpen, setIsOpen] = useState(false);
  const percent =
    section.maxScore > 0
      ? Math.round((section.score / section.maxScore) * 100)
      : 0;

  let headerStatus = "warn";
  if (percent === 100) headerStatus = "pass";
  if (percent === 0) headerStatus = "fail";

  const getStatusText = (status) => {
    return translations.statuses[status] || translations.statuses.warn;
  };

  const getItemLabelText = (key) => {
    return translations.metadataChecks[key] || key;
  };

  return (
    <div className="validation-card">
      <button
        type="button"
        className="section-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="header-left">
          <ChevronIcon isOpen={isOpen} />
          <StatusIcon status={headerStatus} />
          <span className="section-name">
            {translations.dimensions[section.key]}
          </span>
        </div>
        <div className="header-right">
          {section.score}/{section.maxScore} {translations.scoreSuffix}(
          {percent}%)
        </div>
      </button>
      {isOpen && (
        <div className="section-body">
          {section.items.map((item, idx) => (
            <div key={idx} className="validation-item">
              <StatusIcon status={item.status} />
              <span className="item-text">
                <strong className="item-label">
                  {getStatusText(item.status)}: {getItemLabelText(item.label)}
                </strong>
                <span className="item-points">
                  : {item.points}/{item.maxPoints}
                  {translations.scoreSuffix}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function QualityView({ quality, translations }) {
  if (!quality) {
    return null;
  }

  const methodologyUrl = "https://data.europa.eu/mqa/methodology?locale=es";
  const dimensions = quality.breakdown || {};

  const overallPercent = quality.score;

  const getScoreColor = (p) => {
    if (p >= 140) return "#20c530ff";
    if (p >= 100) return "#d4a72c";
    if (p >= 60) return "#d47a2cff";
    return "#cf222e";
  };

  const scoreColor = getScoreColor(overallPercent);

  return (
    <div className="quality-wrapper">
      <div className="quality-summary-card">
        <div className="quality-big-score" style={{ color: scoreColor }}>
          <div className="score-value">
            {quality.score}/{quality.maxScore}
          </div>

          <div className="score-sub">{translations.overallQuality}</div>

          <div className="score-label" style={{ backgroundColor: scoreColor }}>
            {translations.scoreLabels[quality.label]}
          </div>
        </div>

        <div className="quality-bars">
          {Object.entries(dimensions).map(([key, dim]) => (
            <div key={key} className="bar-row">
              <div className="bar-label">{translations.dimensions[key]}</div>

              <div className="bar-track">
                <div
                  className="bar-fill"
                  style={{
                    width: `${(dim.score / dim.max) * 100}%`,
                    backgroundColor: scoreColor,
                  }}
                ></div>
              </div>

              <div className="bar-score">
                {dim.score}/{dim.max}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="quality-description">
        <Trans i18nKey="dataOfferingsDetail.detail.qualityScore.qualityView.summary.description" />
        <a href={methodologyUrl} target="_blank" rel="noopener noreferrer">
          {translations.qualityLink}
        </a>
      </div>
      <h3 className="details-title">{translations.detailsTitle}</h3>

      <div className="validation-section">
        {quality.details &&
          quality.details.map((section, index) => (
            <ValidationGroup
              key={index}
              section={section}
              translations={translations}
            />
          ))}
      </div>
    </div>
  );
}
